"use client";

import Link, { useLinkStatus } from "next/link";
import type { ReactNode } from "react";
import { ArrowUpDown } from "lucide-react";
import type { StationSummary } from "@/hooks/useStationHubData";
import { formatStationLocation } from "@/lib/mobility/station-location";
import { getStationSubtitle } from "@/lib/branches/local-address";
import { cn } from "@/lib/utils";
import { DEFAULT_BRANCH_IMAGE } from "@/lib/branches/image";
import { useHub } from "./hub-context";
import { HUB_GUTTER, Thumb } from "./hub-ui";

/** 지점 머리 카드 썸네일 한 변(px). */
const HEAD_THUMB = 56;

const SORT_LABEL_KEY = {
  price_low: "priceLow",
  price_high: "priceHigh",
  newest: "newest",
} as const;

/**
 * 지점 시트 머리 — 회색 그림자 카드(사진 · 위치 줄 · 이름 · 찾아오는 길).
 * 바이크 지점 시트와 캠핑장 시트가 같이 쓴다. 이름·주소·사진은 상세 조회 결과에만 있다.
 */
export function StationHeadCard({ station }: { station: StationSummary }) {
  const { locale, data } = useHub();
  const detail = data.details.get(station.id);
  // 목록 행과 같은 구성 — 이름 위 위치 줄, 이름 아래 찾아오는 길(없으면 주소).
  // 일본 지점(일본어 외 화면)은 "찾아오는 길 · 번역 주소"(local-address).
  const location = detail ? formatStationLocation(detail, locale) : "";
  const subtitle = detail
    ? getStationSubtitle({
        locale,
        country: detail.country,
        directions: detail.directions,
        localAddress: detail.addressLocal,
        translatedAddress: detail.address,
      })
    : "";

  return (
    // 데스크톱은 목록 카드(지점 행)와 같은 글자 크기로 — 모바일 기준 크기가
    // 목록 카드보다 작아 보였다(09-23).
    <div className="flex items-center gap-3.5 rounded-2xl bg-white p-3.5 shadow-[0_1px_2px_rgba(20,18,12,0.06),0_6px_16px_rgba(20,18,12,0.08)] mapShow:gap-4 mapShow:p-4">
      <Thumb
        src={detail?.imageUrl || DEFAULT_BRANCH_IMAGE}
        alt={detail?.name ?? ""}
        w={HEAD_THUMB}
        h={HEAD_THUMB}
        xl={{ w: 72, h: 72 }}
        rounded="rounded-xl"
        bordered
      />
      <div className="min-w-0 flex-1">
        {location ? (
          <p
            className="mb-0.5 truncate text-[12px] text-gray-500 mapShow:text-[13px]"
            suppressHydrationWarning
          >
            {location}
          </p>
        ) : null}
        {detail ? (
          <h2 className="truncate text-[15px] font-semibold text-gray-900 mapShow:text-[17px]">
            {detail.name}
          </h2>
        ) : (
          <div className="h-4 w-2/5 animate-pulse rounded bg-gray-200" />
        )}
        {subtitle ? (
          <p
            className="mt-0.5 truncate text-[12px] text-gray-500 mapShow:text-[13px]"
            suppressHydrationWarning
          >
            {subtitle}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * 개수(왼쪽) + 정렬(오른쪽, 검색 페이지와 같은 정렬 다이얼로그) 한 줄.
 * 정렬 다이얼로그가 없으면 버튼을 숨긴다.
 */
export function CountSortRow({
  label,
  className,
}: {
  label: string;
  className?: string;
}) {
  const { t, sortBy, onSortOpen } = useHub();
  return (
    <div
      className={cn(
        HUB_GUTTER,
        "flex items-center justify-between gap-3 pt-3 pb-2",
        className,
      )}
    >
      <span className="text-[14px] font-semibold text-gray-900 mapShow:text-base">
        {label}
      </span>
      {onSortOpen ? (
        <button
          type="button"
          onClick={onSortOpen}
          className={cn(
            "-mr-1.5 inline-flex h-8 items-center gap-1 rounded-md px-1.5 text-[13px] text-gray-700 hover:bg-gray-50 mapShow:h-9 mapShow:text-sm",
            sortBy ? "font-semibold text-gray-900" : "font-medium",
          )}
        >
          <ArrowUpDown className="h-3.5 w-3.5" />
          {sortBy ? t(`sort.${SORT_LABEL_KEY[sortBy]}`) : t("filterBar.sort")}
        </button>
      ) : null}
    </div>
  );
}

/**
 * 시트에서 독립 페이지로 가는 한 줄 링크(10-15) — "지점 페이지 보기 ›", "{모델} 전체 보기 ›".
 * 실제 `<a href>` 라 일반 클릭은 그 페이지로 이동하고 새 탭 열기도 된다. 시트 행 클릭과는
 * 무관하다. 이동이 늦으면(프리페치 전 클릭) 글자를 흐리게 해 눌렸음을 보인다 — 상품 링크의
 * 상세 스켈레톤은 지점·모델 페이지 모양이 아니라 쓰지 않는다.
 */
export function SheetPageLink({
  href,
  className,
  children,
}: {
  href: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link
      href={href}
      className={cn(
        "inline-block text-[13px] text-gray-500 hover:text-gray-700 hover:underline",
        className,
      )}
    >
      <PendingText>{children}</PendingText>
    </Link>
  );
}

function PendingText({ children }: { children: ReactNode }) {
  const { pending } = useLinkStatus();
  return (
    <span
      aria-busy={pending || undefined}
      className={cn("transition-opacity", pending && "opacity-50")}
    >
      {children}
    </span>
  );
}
